import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FinanceResetResponse } from '@signalvote/shared';
import { Trash2, Loader2 } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { financeQueries, queryKeys } from '@/lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Demo-only "Reset Finance Data" control (ADR-056). Gated purely on the selected
 * campaign's `isDemo` flag — NOT on platform-admin — so an ordinary member of a
 * demo campaign can wipe its transactional finance data back to a demo-ready
 * state. The backend endpoint 403s any non-demo campaign as defense-in-depth.
 */
export function FinanceResetCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { mutate: resetFinance, isPending } = useMutation({
    mutationFn: async () => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/dev/reset`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Reset failed' }));
        throw new Error(err.error ?? 'Reset failed');
      }
      const reset: FinanceResetResponse = await response.json();
      return reset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(financeQueries);
      setConfirming(false);
    },
  });

  if (!selectedCampaign?.isDemo) return null;

  return (
    <Card className="border-red-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-red-700">
          <Trash2 className="h-4 w-4" />
          Reset Finance Data
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          Clear this demo campaign's transactional finance data (contributions, expenditures,
          contacts, imports, bank transactions, and reports) so it's ready for a fresh demo.
          Settings, processors, bank accounts, and filing deadlines are preserved.
        </p>
        {confirming ? (
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => resetFinance()}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Yes, delete everything'
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(true)}
            className="text-red-700 border-red-300 hover:bg-red-50"
          >
            Reset Finance Data
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
